import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IAuthState, ILoginCredentials, IAuthError, IRegisterData, IAuthResponse, IAuthUser, UserRole, UserStatus } from '../types/auth.types';
import { TokenStorage } from '@/config/storage';
import { useUnifiedServices } from '@/hooks/useUnifiedService';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useTokenSync } from '@/hooks/useTokenSync';
import { hasInvalidTokens } from '@/utils/clearInvalidTokens';
import { isBackendMode, isSupabaseMode } from '@/config/dataSource';
import { supabase } from '@/integrations/supabase/client';

interface IAuthContext {
  state: IAuthState;
  login: (credentials: ILoginCredentials) => Promise<IAuthResponse>;
  register: (data: IRegisterData) => Promise<IAuthResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: IAuthUser) => void;
  profile: IAuthUser | null;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);


interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProviderUnified({ children }: AuthProviderProps): JSX.Element {
  const { auth, dataSource } = useUnifiedServices();

  // Método para buscar perfil com retry e cache
  const fetchUserProfileWithRetry = async (userId: string, maxRetries = 3): Promise<{ profile: any; error: any } | null> => {
    // Primeiro, tenta usar o cache
    try {
      const { getCachedProfile } = await import('@/integrations/supabase/client');
      const cachedResult = await getCachedProfile(userId);
      if (cachedResult && !cachedResult.error) {
        return cachedResult;
      }
    } catch (error) {
      console.warn('[AuthProvider] Erro ao acessar cache, tentando busca direta:', error);
    }

    // Se não há cache ou erro, faz busca com retry
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[AuthProvider] Tentativa ${attempt}/${maxRetries} de buscar perfil do usuário`);

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select(`
            *,
            tenant:tenants(
              id,
              nome
            )
          `)
          .eq('id', userId)
          .single();

        if (profileError) {
          if (attempt === maxRetries) {
            console.error(`[AuthProvider] Erro final ao buscar perfil após ${maxRetries} tentativas:`, profileError);
            return { profile: null, error: profileError };
          }

          console.warn(`[AuthProvider] Erro na tentativa ${attempt}:`, profileError);
          // Aguarda antes da próxima tentativa (backoff exponencial)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          continue;
        }

        return { profile, error: null };
      } catch (error) {
        if (attempt === maxRetries) {
          console.error(`[AuthProvider] Erro final ao buscar perfil após ${maxRetries} tentativas:`, error);
          return { profile: null, error };
        }

        console.warn(`[AuthProvider] Erro na tentativa ${attempt}:`, error);
        // Aguarda antes da próxima tentativa
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }

    return null;
  };
  const [user, setUser] = useState<IAuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<IAuthError | null>(null);
  const [initialized, setInitialized] = useState(false);
  const sessionCheckInterval = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();

  // Hook para renovação automática de token (apenas para backend)
  const { isTokenValid } = useTokenRefresh({
    checkInterval: 5 * 60 * 1000,
    enableBackgroundRefresh: isBackendMode(),
    isAuthenticated: isAuthenticated,
    onTokenRefreshed: () => {
      console.log('[AuthProvider] Token renovado automaticamente');
    },
    onRefreshError: (error) => {
      console.error('[AuthProvider] Erro na renovação automática:', error);
      console.warn('[AuthProvider] Mantendo sessão ativa apesar do erro de renovação');
    },
  });

  // Hook para sincronização de tokens entre abas (apenas para backend)
  const { notifyTokenRefreshed, notifySessionCleared } = useTokenSync({
    enableSync: isBackendMode(),
    onTokenSynced: () => {
      console.log('[AuthProvider] Token sincronizado de outra aba');
    },
    onSessionCleared: () => {
      console.log('[AuthProvider] Sessão limpa em outra aba - mantendo conexão local');
    },
  });

  const verifySession = useCallback(async () => {
    try {
      if (isBackendMode()) {
        // Para backend, verifica se o token é válido localmente
        if (!isTokenValid()) {
          return false;
        }
        TokenStorage.updateLastActivity();
      }

      // Usa o serviço unificado para verificar autenticação
      const isAuth = await auth.isAuthenticated();
      return isAuth;
    } catch (error) {
      console.error('[AuthProvider] Erro ao verificar sessão:', error);
      return false;
    }
  }, [auth, isTokenValid]);

  // Effect para inicializar o estado de autenticação
  useEffect(() => {
    if (initialized) return;

    const initializeAuth = async () => {
      try {
        // Limpa dados antigos do localStorage se existirem
        const pendingUser = localStorage.getItem('pendingUser');
        if (pendingUser) {
          try {
            const parsedUser = JSON.parse(pendingUser);
            // Se o usuário no localStorage não está mais pendente, remove
            if (parsedUser.status && parsedUser.status !== 'PENDING') {
              localStorage.removeItem('pendingUser');
            }
          } catch (error) {
            // Se houver erro ao parsear, remove o item
            localStorage.removeItem('pendingUser');
          }
        }

        if (isBackendMode()) {
          // Para backend, usa o sistema de tokens existente
          const storedUser = TokenStorage.getUser();
          const storedToken = TokenStorage.getAccessToken();

          if (hasInvalidTokens()) {
            console.warn('[AuthProvider] Tokens inválidos detectados, limpando...');
            TokenStorage.clearSession(false);
            setInitialized(true);
            setIsLoading(false);
            return;
          }

          if (!storedToken || !storedUser) {
            console.log('[AuthProvider] Nenhum token ou usuário encontrado');
            setInitialized(true);
            setIsLoading(false);
            return;
          }

          const isSessionValid = await verifySession();

          if (storedUser && isSessionValid) {
            setUser(storedUser);
            setIsAuthenticated(true);
          } else {
            console.warn('[AuthProvider] Sessão inválida na inicialização - limpando tokens');
            TokenStorage.clearSession(false);
          }
        } else if (isSupabaseMode()) {
          // Para Supabase, verifica se há uma sessão ativa
          try {
            // Timeout para evitar travamentos
            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout ao obter sessão')), 10000)
            );

            const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any;

            if (error) {
              console.warn('[AuthProvider] Erro ao obter sessão do Supabase:', error);

              // Fallback: tentar usar dados do localStorage se disponível
              const fallbackUser = localStorage.getItem('multimarcas-auth-token');
              if (fallbackUser) {
                try {
                  const parsedUser = JSON.parse(fallbackUser);
                  if (parsedUser && parsedUser.id) {
                    console.log('[AuthProvider] Usando dados de fallback do localStorage');
                    setUser(parsedUser);
                    setIsAuthenticated(true);
                    setInitialized(true);
                    setIsLoading(false);
                    return;
                  }
                } catch (parseError) {
                  console.warn('[AuthProvider] Erro ao parsear dados de fallback:', parseError);
                }
              }

              return;
            }

            if (session?.user) {
              // Busca o perfil do usuário com dados do tenant (com retry)
              const profileData = await fetchUserProfileWithRetry(session.user.id);

              if (!profileData) {
                console.warn('[AuthProvider] Não foi possível carregar perfil do usuário');
                return;
              }

              const { profile, error: profileError } = profileData;

              if (profileError) {
                console.warn('[AuthProvider] Erro ao buscar perfil:', profileError);
                return;
              }

              // Verificar se o usuário está ativo
              if (profile.status === 'INACTIVE' || profile.deleted_at) {
                console.log('[AuthProvider] Usuário inativo detectado na inicialização, fazendo logout...');
                await supabase.auth.signOut();
                return;
              }

              // Converte o usuário do Supabase para o formato esperado
              const uuidToNumber = (uuid: string) => {
                let hash = 0;
                for (let i = 0; i < uuid.length; i++) {
                  const char = uuid.charCodeAt(i);
                  hash = ((hash << 5) - hash) + char;
                  hash = hash & hash; // Convert to 32bit integer
                }
                return Math.abs(hash);
              };

              const authUser: IAuthUser = {
                id: uuidToNumber(session.user.id),
                uuid: session.user.id, // Armazena o UUID original
                email: session.user.email || '',
                first_name: profile.first_name || '',
                last_name: profile.last_name || '',
                role: (profile.role as UserRole) || UserRole.USER,
                status: (profile as any).status ? (profile as any).status as UserStatus : UserStatus.PENDING, // Usa o status real do banco
                tenant_id: profile.tenant_id ? parseInt(profile.tenant_id) : null,
                tenant: profile.tenant ? {
                  id: profile.tenant.id,
                  nome: profile.tenant.nome
                } : null,
                avatar: profile.avatar || undefined, // Incluir avatar do usuário
              };


              setUser(authUser);
              setIsAuthenticated(true);
            }
          } catch (error) {
            console.warn('[AuthProvider] Erro ao verificar sessão do Supabase:', error);
          }
        }
      } catch (error) {
        console.error('Erro ao inicializar autenticação:', error);
        if (isBackendMode()) {
          TokenStorage.clearSession(false);
        }
      } finally {
        setInitialized(true);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [initialized, verifySession, auth, dataSource.source]);

  // Verificação periódica da sessão
  useEffect(() => {
    if (!isAuthenticated) {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
      return;
    }

    const checkSession = async () => {
      const isValid = await verifySession();
      if (!isValid) {
        console.warn('[AuthProvider] Sessão inválida detectada - mantendo conexão ativa');
      }
    };

    // Verifica a cada 5 minutos
    sessionCheckInterval.current = setInterval(checkSession, 5 * 60 * 1000);

    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
    };
  }, [isAuthenticated, verifySession]);

  // Atualiza a última atividade quando há interação do usuário
  useEffect(() => {
    if (!isAuthenticated || !isBackendMode()) return;

    const updateActivity = () => {
      TokenStorage.updateLastActivity();
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated]);

  const login = async (credentials: ILoginCredentials): Promise<IAuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log(`[AuthProvider] Fazendo login via ${dataSource.source}`);

      const response = await auth.login(credentials);

      if (response.access_token && response.user) {
        if (isBackendMode()) {
          // Para backend, salva tokens no localStorage
          TokenStorage.setTokens(response.access_token, response.refresh_token || '');
          TokenStorage.setUser(response.user);

          // Notifica outras abas sobre o login
          notifyTokenRefreshed(response.access_token, response.refresh_token || '');
        } else if (isSupabaseMode()) {
          // Para Supabase, converte o usuário para o formato esperado
          // Converte UUID para número usando hash simples
          const uuidToNumber = (uuid: string) => {
            let hash = 0;
            for (let i = 0; i < uuid.length; i++) {
              const char = uuid.charCodeAt(i);
              hash = ((hash << 5) - hash) + char;
              hash = hash & hash; // Convert to 32bit integer
            }
            return Math.abs(hash);
          };

          const authUser: IAuthUser = {
            id: uuidToNumber(response.user.id),
            uuid: response.user.id, // Armazena o UUID original
            email: response.user.email || '',
            first_name: response.user.user_metadata?.first_name || '',
            last_name: response.user.user_metadata?.last_name || '',
            role: UserRole.USER, // Será atualizado pelo perfil
            status: UserStatus.PENDING, // Novos usuários começam como PENDING
            tenant_id: null,
          };

          // Busca o perfil completo do usuário
          const profile = await auth.getCurrentUser();
          if (profile) {
            authUser.id = uuidToNumber(profile.id);
            authUser.role = profile.role as UserRole;
            authUser.tenant_id = profile.tenantId || null;
            authUser.status = (profile.status as UserStatus) || UserStatus.PENDING;
            authUser.first_name = profile.firstName;
            authUser.last_name = profile.lastName;
          }

          response.user = authUser;
        }

        setUser(response.user);
        setIsAuthenticated(true);

        // Para Supabase, também salva o token no localStorage para compatibilidade
        if (isSupabaseMode() && response.access_token) {
          localStorage.setItem('accessToken', response.access_token);
          localStorage.setItem('refreshToken', response.refresh_token || '');
        }

        // Se o usuário estiver pendente, salva no localStorage para a página de aguardando liberação
        if (response.user.status === 'PENDING') {
          // Converte o ID para string antes de salvar no localStorage
          const userToSave = {
            ...response.user,
            id: response.user.id.toString()
          };
          localStorage.setItem('pendingUser', JSON.stringify(userToSave));
        }
      } else {
        if (response.message) {
          const authError = {
            message: response.message,
            code: response.status || 'AUTH_ERROR'
          };
          setError(authError);
        }
      }

      return response;
    } catch (error: any) {
      console.error('[AuthProvider] Erro no login:', error);
      const authError = {
        message: error.message || 'Erro ao fazer login',
        code: error.code || 'AUTH_ERROR'
      };
      setError(authError);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: IRegisterData): Promise<IAuthResponse> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log(`[AuthProvider] Registrando via ${dataSource.source}`);

      const response = await auth.register(data);

      if (response.access_token && response.user) {
        if (isBackendMode()) {
          TokenStorage.setTokens(response.access_token, response.refresh_token || '');
          TokenStorage.setUser(response.user);
        } else if (isSupabaseMode()) {
          // Para Supabase, converte o usuário para o formato esperado
          // Converte UUID para número usando hash simples
          const uuidToNumber = (uuid: string) => {
            let hash = 0;
            for (let i = 0; i < uuid.length; i++) {
              const char = uuid.charCodeAt(i);
              hash = ((hash << 5) - hash) + char;
              hash = hash & hash; // Convert to 32bit integer
            }
            return Math.abs(hash);
          };

          const authUser: IAuthUser = {
            id: uuidToNumber(response.user.id),
            uuid: response.user.id, // Armazena o UUID original
            email: response.user.email || '',
            first_name: response.user.user_metadata?.first_name || '',
            last_name: response.user.user_metadata?.last_name || '',
            role: UserRole.USER,
            status: UserStatus.PENDING, // Novos usuários registrados começam como PENDING
            tenant_id: null,
          };

          // Busca o perfil completo do usuário para obter status correto
          const profile = await auth.getCurrentUser();
          if (profile) {
            authUser.id = uuidToNumber(profile.id);
            authUser.role = profile.role as UserRole;
            authUser.tenant_id = profile.tenantId || null;
            authUser.status = (profile.status as UserStatus) || UserStatus.PENDING;
            authUser.first_name = profile.firstName;
            authUser.last_name = profile.lastName;
          }

          response.user = authUser;
        }

        setUser(response.user);
        setIsAuthenticated(true);

        // Para Supabase, também salva o token no localStorage para compatibilidade
        if (isSupabaseMode() && response.access_token) {
          localStorage.setItem('accessToken', response.access_token);
          localStorage.setItem('refreshToken', response.refresh_token || '');
        }

        // Se o usuário estiver pendente, salva no localStorage para a página de aguardando liberação
        if (response.user.status === 'PENDING') {
          // Converte o ID para string antes de salvar no localStorage
          const userToSave = {
            ...response.user,
            id: response.user.id.toString()
          };
          localStorage.setItem('pendingUser', JSON.stringify(userToSave));
        }
      } else {
        if (response.message) {
          const authError = {
            message: response.message,
            code: response.status || 'AUTH_ERROR'
          };
          setError(authError);
        }
      }

      return response;
    } catch (error: any) {
      console.error('[AuthProvider] Erro no registro:', error);
      const authError = {
        message: error.message || 'Erro ao registrar',
        code: error.code || 'AUTH_ERROR'
      };
      setError(authError);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log(`[AuthProvider] Fazendo logout via ${dataSource.source}`);

      if (isAuthenticated) {
        await auth.logout();
      }
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      if (isBackendMode()) {
        // Para backend, limpa tokens do localStorage
        TokenStorage.clearSession(true);
        notifySessionCleared();
      }

      setUser(null);
      setIsAuthenticated(false);
      setError(null);

      navigate('/autenticacao', { replace: true });
    }
  };

  const clearError = () => {
    setError(null);
  };

  const updateUser = (updatedUser: IAuthUser) => {
    if (isBackendMode()) {
      TokenStorage.setUser(updatedUser);
    }
    setUser(updatedUser);
  };

  const state: IAuthState = {
    user,
    isAuthenticated,
    isLoading,
    error,
  };

  const contextValue: IAuthContext = {
    state,
    login,
    register,
    logout,
    clearError,
    updateUser,
    profile: user,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export default AuthProviderUnified;
